/**
 * The one place the browser talks to Medulla.
 *
 * Every call sends the session cookie and understands the RFC 9457 problem
 * documents the API returns, so callers get a typed error with a stable `code`
 * instead of having to unpick a response body.
 */

export type ProblemDocument = {
  type?: string
  title?: string
  status: number
  detail?: string
  /** Stable machine-readable identifier, e.g. "user.email_already_in_use". */
  code?: string
  context?: Record<string, unknown>
  /** Symfony validation failures arrive under this key. */
  violations?: Array<{ propertyPath: string; message: string }>
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDocument,
  ) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
  }

  get code(): string | undefined {
    return this.problem.code
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** Field-level messages, keyed by property, for wiring straight into a form. */
  get fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {}
    for (const violation of this.problem.violations ?? []) {
      errors[violation.propertyPath] ??= violation.message
    }
    return errors
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** PATCH needs merge-patch; everything else is plain JSON. */
  contentType?: string
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null | readonly string[]>
}

export type Paginated<T> = {
  items: T[]
  totalItems: number
  page: number
  perPage: number
  totalPages: number
  /**
   * The response's headers, for an endpoint that sends a count of its own
   * beside the paging ones - the tasks carrying a tag say how many more are
   * on projects the viewer cannot open this way.
   */
  headers?: Headers
}

const BASE = '/api/v1'

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`
  if (!query) return url

  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue

    if (Array.isArray(value)) {
      // Repeated as `key[]=a&key[]=b`, which PHP parses into an array.
      // URLSearchParams would otherwise join them with a comma into one
      // value that means nothing to the server.
      for (const item of value) {
        if (item !== '') params.append(`${key}[]`, String(item))
      }

      continue
    }

    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function toProblem(response: Response): Promise<ProblemDocument> {
  try {
    const body = await response.json()
    return typeof body === 'object' && body !== null
      ? { status: response.status, ...body }
      : { status: response.status }
  } catch {
    // A proxy error or a crash before the error handler runs returns HTML.
    return { status: response.status, title: response.statusText }
  }
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, contentType = 'application/json', query, signal } = options

  const response = await fetch(buildUrl(path, query), {
    method,
    signal,
    // Without this the session cookie is not sent and every call is a 401.
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': contentType }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    throw new ApiError(response.status, await toProblem(response))
  }

  return response
}

export async function apiGet<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
  const response = await request(path, { ...options, method: 'GET' })
  return response.json() as Promise<T>
}

/** GET a collection, reading the pagination the API puts in response headers. */
export async function apiGetPage<T>(
  path: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
): Promise<Paginated<T>> {
  const response = await request(path, { ...options, method: 'GET' })
  const items = (await response.json()) as T[]

  const header = (name: string, fallback: number): number => {
    const raw = response.headers.get(name)
    const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  return {
    items,
    totalItems: header('X-Total-Count', items.length),
    page: header('X-Page', 1),
    perPage: header('X-Per-Page', items.length),
    totalPages: header('X-Total-Pages', 1),
    headers: response.headers,
  }
}

export async function apiSend<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T | null> {
  const response = await request(path, {
    method,
    body,
    contentType: method === 'PATCH' ? 'application/merge-patch+json' : 'application/json',
  })

  if (response.status === 204) return null
  return response.json() as Promise<T>
}

/**
 * A multipart upload.
 *
 * Deliberately not routed through `request()`: FormData must go out without a
 * Content-Type header so the browser can set one including the multipart
 * boundary, and setting it by hand produces a body the server cannot parse.
 * Error handling is otherwise identical, so a failed upload throws the same
 * ApiError with the same stable `code` as anything else.
 */
export async function apiUpload<T>(path: string, body: FormData): Promise<T | null> {
  const response = await fetch(`/api/v1${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    body,
  })

  if (!response.ok) {
    let problem: ProblemDocument = { status: response.status }

    try {
      problem = { status: response.status, ...(await response.json()) }
    } catch {
      /* A proxy error returns HTML; the status alone will have to do. */
    }

    throw new ApiError(response.status, problem)
  }

  if (response.status === 204) return null

  return response.json() as Promise<T>
}
